import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import { api } from "../lib/api";
import { Check, ArrowRight, X } from "lucide-react";
import SEOHead from "../components/SEOHead";

import { useTranslation } from 'react-i18next';
export default function CheckoutSuccess() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ state: "polling", message: "Verifying payment..." });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus({ state: "error", message: "Missing session id." });
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus({ state: "success", message: "Payment successful.", data });
          return;
        }
        if (data.status === "expired") {
          setStatus({ state: "error", message: "Payment session expired." });
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= 6) {
          setStatus({ state: "pending", message: "Still processing — check Dashboard later.", data });
          return;
        }
        timer = setTimeout(poll, 2000);
      } catch {
        setStatus({ state: "error", message: "Could not verify payment." });
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  const isSuccess = status.state === "success";
  const isError = status.state === "error";

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <SEOHead
        title={t('checkout_success.payment_successful_max_employee')}
        description={t('checkout_success.your_payment_was_successful_your_max_employee')}
        url="/checkout/success"
        noIndex
      />
      <Header />
      <div className="max-w-3xl mx-auto px-6 lg:px-12 py-24">
        <div className="border border-zinc-900 bg-zinc-950 p-12 text-center">
          <div className={`w-16 h-16 mx-auto mb-8 flex items-center justify-center ${
            isSuccess ? "bg-amber-500" : isError ? "bg-red-600" : "bg-zinc-800"
          }`}>
            {isSuccess ? <Check className="w-8 h-8 text-black" /> :
             isError ? <X className="w-8 h-8 text-white" /> :
             <div className="w-6 h-6 border-2 border-zinc-400 border-t-amber-500 animate-spin" />}
          </div>

          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-3">
            // {isSuccess ? "TRANSACTION COMPLETE" : isError ? "TRANSACTION FAILED" : "TRANSACTION PROCESSING"}
          </div>
          <h1 data-testid="checkout-status-title" className="font-display text-4xl uppercase mb-4">
            {isSuccess ? "Payment received." : isError ? "Something went wrong." : "Verifying..."}
          </h1>
          <p data-testid="checkout-status-message" className="text-zinc-400 max-w-md mx-auto leading-relaxed mb-8">
            {status.message}
          </p>

          {sessionId && (
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600 mb-8 break-all">
              SESSION: {sessionId}
            </div>
          )}

          <div className="flex justify-center gap-4 flex-wrap">
            <Link
              to="/dashboard"
              data-testid="goto-dashboard"
              className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black px-6 py-3 font-bold uppercase tracking-wider text-sm transition-colors"
            >
              Go to Dashboard <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/pricing"
              data-testid="back-to-products"
              className="inline-flex items-center gap-2 border border-zinc-800 hover:border-zinc-500 text-white px-6 py-3 font-bold uppercase tracking-wider text-sm transition-colors"
            >
              Back to catalog
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
